import { useState, useRef } from 'react'
import { Props_SnippetVideoAndTextButtons } from './VideoAndTextButtons.props'
import { Legacy_CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import SnippetLink from '../Shared/Link/Link'

type VideoProvider = 'youtube' | 'vimeo' | 'video' | null

const getVideoMimeType = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase()
    switch (extension) {
        case 'mp4':
            return 'video/mp4'
        case 'webm':
            return 'video/webm'
        case 'ogg':
        case 'ogv':
            return 'video/ogg'
        case 'mov':
            return 'video/quicktime'
        default:
            return 'video/mp4'
    }
}

export const getVideoProvider = (url: string): VideoProvider => {
    if (!url) return null;
    if (url.includes('youtube') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo')) return 'vimeo';
    return 'video';
};

export const getEmbedUrl = (url: string, provider: VideoProvider): string => {
    if (provider === 'youtube') {
        let videoId = '';
        if (url.includes('youtu.be')) {
            videoId = url.split('/').pop()?.split('?')[0] || '';
        } else if (url.includes('youtube.com/watch')) {
            const params = new URLSearchParams(url.split('?')[1]);
            videoId = params.get('v') || '';
        } else if (url.includes('youtube.com/embed')) {
            const hasAutoplay = url.includes('autoplay=1');
            if (!hasAutoplay) {
                const separator = url.includes('?') ? '&' : '?';
                return `${url}${separator}autoplay=1`;
            }
            return url;
        }
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    
    const hasAutoplay = url.includes('autoplay=1');
    if (!hasAutoplay) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}autoplay=1`;
    }
    return url;
};


export const SnippetVideoAndTextButtons = (props: Props_SnippetVideoAndTextButtons) => {
    const { heading, content, repeatable_link, video, linkProps } = props

    if (!video?.url?.value) return null;
    
    const videoProvider = getVideoProvider(video.url.value);
    const embedUrl = getEmbedUrl(video.url.value, videoProvider as VideoProvider);
    
    const [playing, setPlaying] = useState<boolean>(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)

    const snippetVideoClass = 'snippet-video-and-text-buttons'

    const classHeadingVisibility: string = heading.visibility?.value === "true" ? `` : `${snippetVideoClass}__heading--hidden`
    const classHeadingAlignment: string = heading.alignment?.value ? `${snippetVideoClass}__heading--alignment-${heading.alignment.value}` : ``
    const classContentPosition: string = content.position?.value ? `${snippetVideoClass}--position-${content.position.value}` : ``
    const classContentAlignment: string = content.alignment?.value ? `${snippetVideoClass}--alignment-${content.alignment.value}` : ``
    const classContentVerticalAlignment: string = content.vertical_alignment?.value ? `${snippetVideoClass}--vertical-alignment-${content.vertical_alignment.value}` : ``
    const classVideoPlaying: string = playing ? `${snippetVideoClass}--playing` : ``
    const classVideoFullscreen: string = video.fullscreen?.value === "true" ? `${snippetVideoClass}--fullscreen` : ``
    const classLinkAlignment: string = linkProps.alignment?.value ? `${snippetVideoClass}__container-links--${linkProps.alignment.value}` : ``

    const renderContent = heading.visibility.value === "true" || content.content.value?.length || repeatable_link[0].linkHref?.value?.length;
    return (
        <section className={`${snippetVideoClass} ${classVideoPlaying} ${classVideoFullscreen} ${classContentPosition} ${classContentAlignment} ${classContentVerticalAlignment}`}>
            <div className={`${snippetVideoClass}__container`}>
                {renderContent ? ( 
                        <div className={`${snippetVideoClass}__container-content`}>
                        <h2 className={`${snippetVideoClass}__heading ${classHeadingVisibility} ${classHeadingAlignment}`}>{heading?.content?.value}</h2>
                        <div className={`${snippetVideoClass}__content`}>
                            { content.content?.value && Legacy_CompileMarkdown(content?.content?.value)}
                        </div>
                            
                        <div className={`${snippetVideoClass}__container-links ${classLinkAlignment}`}>
                            { repeatable_link?.map((link, index) => (
                                <SnippetLink
                                    key={`link-${index}`}
                                    url={link.linkHref?.value ?? ''}
                                    className={`${snippetVideoClass}__link`}
                                    linkStyles={link.linkStyle?.value}
                                    target={link.linkTarget?.value === "true"}
                                    rel={link.linkTarget?.value === "true" ? 'noopener noreferrer' : undefined}
                                >
                                    {link.linkContent?.value ?? ''}
                                </SnippetLink>
                            ))}
                        </div>
                    </div>
                ) : null}
                
                <div className={`${snippetVideoClass}__container-video-player`}>
                    { video?.thumbnail?.value && (
                        <figure className={`${snippetVideoClass}__container-thumbnail`}>
                            <img
                                src={video.thumbnail.value}
                                alt={video?.alt?.value || ''}
                                className={`${snippetVideoClass}__thumbnail`}
                                loading="lazy"
                                width="641"
                                height="641"
                            />
                        </figure>
                    )}
                    { video?.url?.value && (
                        <div className={`${snippetVideoClass}__container-video`}>
                            { video?.thumbnail?.value && (
                                <button
                                    className={`${snippetVideoClass}__button-play`}
                                    aria-label={`{{js.snippet.video.button.play.aria-label}}`}
                                    onClick={() => setPlaying(true)}
                                >
                                </button>
                            )}
                            { embedUrl && playing && (videoProvider === 'youtube' || videoProvider === 'vimeo') && (
                                <iframe
                                    className={`${snippetVideoClass}__video`}
                                    src={embedUrl}
                                    width="1280"
                                    height="720"
                                    title="Video player"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy={videoProvider === 'youtube' ? 'strict-origin-when-cross-origin' : undefined}
                                    allowFullScreen
                                ></iframe>
                            )}
                            { playing && videoProvider === 'video' && (
                                <video
                                    className={`${snippetVideoClass}__video`}
                                    controls
                                    controlsList='nodownload'
                                    width="1280"
                                    height="720"
                                    ref={videoRef}
                                    autoPlay
                                >
                                    <track kind="captions" />
                                    <source src={video.url.value} type={getVideoMimeType(video.url.value)} />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}
